import { formatDieValue } from './dice';
import { playSound } from '../../utils/utils';
import { getBoardSquareLabel } from './doorBoard';
import { RELICS } from './relics';
import './GuardianDamageCalculation.css';

const GuardianDamageCalculation = ({ result, ...presentationProps }) => {
  const calculation =
    result?.damageCalculation ||
    (result?.boss
      ? {
          initialRoll: result.naturalRoll,
          finalRoll:
            result.attackDamageBeforeShield ??
            result.attackDealt ??
            result.dealt ??
            0,
          boardSteps: [],
          relicEffects: [],
        }
      : null);
  if (!calculation) return null;
  const overkill = Math.max(0, calculation.finalRoll - result.attackDealt);
  return (
    <section
      {...presentationProps}
      className="coreboundAttackRecap"
      aria-label="Last attack"
    >
      <div className="coreboundAttackRecapOutcome" role="status">
        {result.frozen && (
          <span>TRAP · Die frozen for one turn · Enemy attacks twice</span>
        )}
        <strong>{formatDieValue(result.attackDealt)} damage dealt</strong>
        <span>
          {result.damage > 0 ? `You lost ${result.damage} HP` : 'No HP lost'}
        </span>
      </div>
      <details
        className="coreboundAttackBreakdown"
        onToggle={(event) =>
          playSound(
            event.currentTarget.open
              ? '/sounds/menu-select-2.mp3'
              : '/sounds/menu-select-3.mp3'
          )
        }
      >
        <summary>
          Damage breakdown <span aria-hidden="true">⌄</span>
        </summary>
        <div
          className="coreboundAttackBreakdownPanel"
          role="region"
          aria-label="Last attack damage calculations"
          tabIndex={0}
        >
          <header>
            <small>LAST ATTACK · {result.enemy?.name}</small>
            <strong>
              {formatDieValue(calculation.finalRoll)} attack power
            </strong>
          </header>
          <dl>
            {calculation.rows?.map((row, index) => (
              <div key={index}>
                <dt>
                  {row.label}
                  <small>{row.detail}</small>
                </dt>
                <dd>{row.value}</dd>
              </div>
            ))}
            {!calculation.rows && (
              <div>
                <dt>Initial die roll</dt>
                <dd>{formatDieValue(result.naturalRoll)}</dd>
              </div>
            )}
            {calculation.flipped && (
              <div>
                <dt>Mirror Chip</dt>
                <dd>
                  {formatDieValue(result.naturalRoll)} →{' '}
                  {formatDieValue(calculation.initialRoll)}
                </dd>
              </div>
            )}
            {calculation.boardSteps.map((step, index) => (
              <div key={`${step.index}:${index}`}>
                <dt>
                  Tile {step.index + 1} · {step.label}
                </dt>
                <dd>
                  {formatDieValue(step.before)} → {formatDieValue(step.value)}
                </dd>
              </div>
            ))}
            {calculation.relicEffects.map((effect) => (
              <div key={effect.id}>
                <dt>{effect.name}</dt>
                <dd>+{formatDieValue(effect.bonus)}</dd>
              </div>
            ))}
            <div className="is-total">
              <dt>Final attack power</dt>
              <dd>{formatDieValue(calculation.finalRoll)}</dd>
            </div>
            <div>
              <dt>Damage dealt</dt>
              <dd>{formatDieValue(result.attackDealt)}</dd>
            </div>
            {overkill > 0 && (
              <div>
                <dt>Excess power beyond enemy HP</dt>
                <dd>{formatDieValue(overkill)}</dd>
              </div>
            )}
            <div>
              <dt>Enemy HP</dt>
              <dd>
                {formatDieValue(result.enemyHpBefore)} →{' '}
                {formatDieValue(result.enemyHpAfter)}
              </dd>
            </div>
            {result.bossSelfDamage > 0 && (
              <div>
                <dt>Core skull tile damage</dt>
                <dd>{result.bossSelfDamage}</dd>
              </div>
            )}
            {result.bossHealed > 0 && (
              <div>
                <dt>Core HP restored</dt>
                <dd>+{result.bossHealed}</dd>
              </div>
            )}
            <div>
              <dt>Retaliation</dt>
              <dd>
                {result.retaliation
                  ? `${result.counterDamage} damage`
                  : 'None · enemy defeated'}
              </dd>
            </div>
            {result.boss && (
              <>
                <div>
                  <dt>Core attack</dt>
                  <dd>
                    {result.bossAttackType === 'massiveAttack'
                      ? 'MASSIVE ATTACK'
                      : result.attack}
                  </dd>
                </div>
                <div>
                  <dt>
                    Core roll
                    <small>Before Interference, backlash, and defenses</small>
                  </dt>
                  <dd>
                    {formatDieValue(result.bossRoll)} →{' '}
                    {formatDieValue(
                      result.bossEffectiveRoll ?? result.bossRoll
                    )}{' '}
                    damage
                  </dd>
                </div>
                {result.bossBoardLanding && (
                  <div>
                    <dt>Core landed on</dt>
                    <dd>
                      {getBoardSquareLabel({
                        ...result.bossBoardLanding,
                        coreDamage: true,
                      })}
                    </dd>
                  </div>
                )}
                <div>
                  <dt>Counterattack outcome</dt>
                  <dd>
                    {result.protocolSaved
                      ? 'Last Stand · survived at 1 HP'
                      : result.decoyIntercepted && result.damage === 0
                      ? 'Decoy intercepted · no HP lost'
                      : !result.hit
                      ? 'Miss · no damage'
                      : result.damage === 0
                      ? 'Blocked · no damage'
                      : `${result.damage} damage received`}
                  </dd>
                </div>
                {result.decoyStillDeployed && (
                  <div>
                    <dt>Decoy Beacon</dt>
                    <dd>Still deployed for the next damaging attack</dd>
                  </div>
                )}
              </>
            )}
            {calculation.counterRows?.map((row, index) => (
              <div key={`counter-${index}`}>
                <dt>
                  {row.label}
                  <small>{row.detail}</small>
                </dt>
                <dd>{row.value}</dd>
              </div>
            ))}
            {(calculation.defenses || []).map(({ name, amount }) => (
              <div key={name}>
                <dt>{name}</dt>
                <dd>−{amount}</dd>
              </div>
            ))}
            {!calculation.defenses && result.boardShieldBlocked > 0 && (
              <div>
                <dt>Board shield blocked</dt>
                <dd>{result.boardShieldBlocked}</dd>
              </div>
            )}
            {result.boardDamage > 0 && (
              <div>
                <dt>Board hazard damage</dt>
                <dd>{result.boardDamage}</dd>
              </div>
            )}
            {result.healed > 0 && (
              <div>
                <dt>HP restored</dt>
                <dd>+{result.healed}</dd>
              </div>
            )}
            {result.maxHpGained > 0 && (
              <div>
                <dt>Maximum HP gained</dt>
                <dd>+{result.maxHpGained}</dd>
              </div>
            )}
            {result.boss &&
              (result.relicActivations || [])
                .filter((effect) => !effect.bonus)
                .map((effect, index) => (
                  <div key={`activation-${effect.id}-${index}`}>
                    <dt>
                      Relic ·{' '}
                      {effect.name || RELICS[effect.id]?.name || effect.id}
                    </dt>
                    <dd>{effect.message}</dd>
                  </div>
                ))}
            {result.protocolSaved && (
              <div>
                <dt>Last Stand Protocol</dt>
                <dd>Survived at 1 HP</dd>
              </div>
            )}
            <div className="is-total">
              <dt>Your HP</dt>
              <dd>
                {result.playerHpBefore} → {result.playerHpAfter}
              </dd>
            </div>
          </dl>
        </div>
      </details>
    </section>
  );
};

export default GuardianDamageCalculation;
